import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Brain, Eye, Network, GraduationCap, FileText, Globe,
  ArrowRight, Target, Lightbulb, Building2, Award,
  ScrollText, Briefcase, BookOpen, UserCheck
} from "lucide-react";
import ExecutiveHero from "@/components/ExecutiveHero";
import { OFFICIAL_SEAL, FOUNDER_PORTRAIT } from "@/lib/brand";

const ABOUT_HERO = "https://admin-dash-repair-2.preview.emergentagent.com/api/media/file/08bcc2b0-bf0b-4c28-a5bf-1800cd82ece8";
const FOUNDER_IMG = FOUNDER_PORTRAIT;
const SEAL = OFFICIAL_SEAL;

const ECOSYSTEM = [
  { icon: Brain,        title: "WAIT Theory",     description: "Foundational intelligence framework",     href: "/wait-theory" },
  { icon: FileText,     title: "SCFR-Journal",    description: "Peer research and doctrinal publication", href: "/scfr-journal" },
  { icon: Building2,    title: "JusWork",         description: "Workforce justice & opportunity",         href: "/juswork" },
  { icon: GraduationCap,title: "Zodiac LMS-Lab",  description: "Hybrid-intelligence learning",            href: "/ecosystem/zodiac-lms-lab" },
  { icon: Eye,          title: "Observatory",     description: "Workforce intelligence dashboards",       href: "/observatories/global" },
  { icon: Globe,        title: "Global Network",  description: "International partnerships",              href: "/ecosystem/partnership" },
];

const PILLARS = [
  { icon: Target,    title: "Measurable Intelligence",  description: "Beyond credentials — quantifiable workforce intelligence metrics." },
  { icon: Network,   title: "Hybrid AI-Human Systems",  description: "Convergence of artificial intelligence with human capability." },
  { icon: Building2, title: "Institutional Modernization", description: "Transforming organizational structures for the next decade." },
  { icon: Lightbulb, title: "Doctrinal Leadership",     description: "Authoritative frameworks for workforce intelligence science." },
];

export default function About() {
  return (
    <div className="min-h-screen zodiac-page" data-testid="about-page">
      <ExecutiveHero
        eyebrow="About the Institute"
        title={<>Engineering the future of</>}
        accentTitle="workforce intelligence."
        subtitle="The Zodiac Workforce Intelligence Institute integrates research, doctrine, programs, and instrumentation into one premium institutional ecosystem — engineered for executive transformation in the Human-AI era."
        image={ABOUT_HERO}
        actions={[
          { label: "Explore WAIT Theory", href: "/wait-theory", testId: "about-wait-cta" },
          { label: "Contact Institute", href: "/contact", variant: "ghost", testId: "about-contact-cta" },
        ]}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "About" }]}
      />

      {/* Founder portrait + quote */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20 overflow-hidden" data-testid="about-founder">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[360px_1fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mx-auto lg:mx-0"
          >
            <div className="relative w-72 h-72 lg:w-80 lg:h-80">
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(15,30,71,0.18)] ring-1 ring-[#0F1E47]/10">
                <img src={FOUNDER_IMG} alt="Dr. Spencer W. Charles" className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-5 py-3 bg-white rounded-xl shadow-[0_8px_30px_rgba(15,30,71,0.15)] border border-[#E5E7EB] text-center whitespace-nowrap">
                <p className="font-semibold text-[#0F1E47] text-sm">Dr. Spencer W. Charles, PhD</p>
                <p className="text-[11px] text-[#6B7280] uppercase tracking-widest">Founder · President</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#991B1B]" />
              From the Founder
            </div>
            <blockquote className="zodiac-heading text-2xl sm:text-3xl lg:text-[2.3rem] leading-[1.35] text-[#0F1E47] italic">
              "The future of work demands more than credentials and certifications. It demands
              <span className="not-italic text-[#991B1B] font-semibold"> measurable intelligence</span> —
              the ability to quantify, develop, and align human capability with the transformative
              power of artificial intelligence."
            </blockquote>
            <div className="zodiac-divider-gold mt-8" />
          </motion.div>
        </div>

        {/* Founder Full Biography */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20" data-testid="about-founder-biography">
          <div className="zodiac-eyebrow mb-3 flex items-center gap-3 justify-center">
            <span className="w-8 h-px bg-[#1E3A8A]" />
            Founder Biography
            <span className="w-8 h-px bg-[#1E3A8A]" />
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] text-center mb-12 leading-tight">
            Dr. Spencer W. Charles, <span className="italic text-[#991B1B] font-normal">PhD</span>
          </h2>

          <div className="grid lg:grid-cols-2 gap-6">
            <BioBlock icon={UserCheck} title="Professional Summary" accent="#1E3A8A">
              <ul className="space-y-1.5 text-[#475569]">
                {[
                  "Workforce Intelligence",
                  "Workforce Development",
                  "Leadership Development",
                  "Quality Management",
                  "Human-AI Workforce Integration",
                  "Education and Training",
                  "Research and Institutional Development",
                ].map((d) => (
                  <li key={d} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C8A157]" />{d}
                  </li>
                ))}
              </ul>
            </BioBlock>

            <BioBlock icon={GraduationCap} title="Academic Background" accent="#991B1B">
              <div className="text-[#475569] text-sm leading-relaxed">
                <p className="text-[#0F1E47] font-semibold mb-1">PhD · Leadership Studies</p>
                <p>Our Lady of the Lake University</p>
                <p className="mt-3 text-xs uppercase tracking-widest text-[#6B7280]">
                  Doctoral research focused on workforce intelligence, institutional leadership,
                  and credentialing reform.
                </p>
              </div>
            </BioBlock>

            <BioBlock icon={Brain} title="Research Areas" accent="#C8A157">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-[#475569]">
                {[
                  "Workforce Intelligence",
                  "WAIT Theory",
                  "Human-AI Workforce Integration",
                  "Credential Fatigue",
                  "Workforce Development",
                  "Leadership and Service Quality",
                  "Organizational Performance",
                  "Institutional Performance",
                ].map((d) => (
                  <div key={d} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1E3A8A] mt-1.5 flex-shrink-0" />{d}
                  </div>
                ))}
              </div>
            </BioBlock>

            <BioBlock icon={Briefcase} title="Institutional Roles" accent="#0F766E">
              <ul className="space-y-3 text-[#475569] text-sm">
                <li>
                  <p className="text-[#0F1E47] font-semibold">Founder & President</p>
                  <p className="text-xs text-[#6B7280]">Zodiac Workforce Intelligence Institute</p>
                </li>
                <li>
                  <p className="text-[#0F1E47] font-semibold">Editor-in-Chief</p>
                  <p className="text-xs text-[#6B7280]">Society & Credential Fatigue Review (SCFR)</p>
                </li>
              </ul>
            </BioBlock>
          </div>

          {/* Founder vision quote */}
          <div className="mt-10 p-8 sm:p-10 rounded-2xl bg-[#0F1E47] text-white relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#C8A157]/12 blur-3xl" />
            <div className="relative">
              <ScrollText className="w-8 h-8 text-[#C8A157] mb-4" />
              <blockquote className="zodiac-heading text-xl sm:text-2xl lg:text-[1.7rem] italic leading-[1.45]">
                "Behind every organizational result lies a hidden architecture of trust, leadership,
                capability, and alignment. Those who learn to <span className="text-[#C8A157] not-italic font-semibold">measure it</span> will shape the future of work."
              </blockquote>
              <div className="mt-6 text-[#C8A157] text-[11px] uppercase tracking-[0.2em] font-semibold">
                — Founder Vision Statement
              </div>
            </div>
          </div>

          {/* Action area */}
          <div className="mt-10 flex flex-wrap justify-center gap-3" data-testid="founder-action-area">
            <Link to="/founder" data-testid="cta-view-founder-profile">
              <button className="h-12 px-6 rounded-md bg-[#1E3A8A] text-white hover:bg-[#15307A] font-semibold inline-flex items-center gap-2 shadow-[0_4px_14px_rgba(30,58,138,0.25)]">
                <UserCheck className="w-4 h-4" /> View Founder Profile
              </button>
            </Link>
            <Link to="/research" data-testid="cta-research-publications">
              <button className="h-12 px-6 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-white inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4" /> Research & Publications
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission pillars */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#1E3A8A]" />
              Institutional Mission
            </div>
            <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold leading-tight">
              Four pillars define the <span className="italic text-[#991B1B] font-normal">institutional doctrine</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="zodiac-hairline-card p-6"
              >
                <div className="w-11 h-11 rounded-lg bg-[#1E3A8A]/[0.08] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-2">{p.title}</h3>
                <p className="text-sm text-[#6B7280] leading-relaxed">{p.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem grid */}
      <section className="bg-[#FBFAF7] border-y border-[#E5E7EB] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <div className="zodiac-eyebrow mb-3 flex items-center gap-3">
              <span className="w-8 h-px bg-[#C8A157]" />
              Integrated Ecosystem
            </div>
            <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold leading-tight">
              Six nodes. <span className="italic text-[#991B1B] font-normal">One workforce-intelligence ecosystem.</span>
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ECOSYSTEM.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Link to={item.href} className="zodiac-hairline-card group flex items-start gap-4 p-6 h-full">
                  <div className="w-12 h-12 rounded-lg bg-[#0F1E47]/[0.06] flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#0F1E47]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-1 group-hover:text-[#991B1B] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[#6B7280]">{item.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#991B1B] group-hover:translate-x-1 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing seal + CTA */}
      <section className="bg-white py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img
            src={SEAL}
            alt="Zodiac Workforce Intelligence Institute Official Seal"
            className="w-40 h-40 sm:w-44 sm:h-44 mx-auto mb-8 drop-shadow-[0_8px_24px_rgba(15,30,71,0.18)]"
            data-testid="about-closing-seal"
          />
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-4 leading-tight">
            Join the future of <span className="italic text-[#991B1B] font-normal">workforce intelligence.</span>
          </h2>
          <p className="text-[#475569] mb-8 leading-relaxed">
            Connect with the Zodiac Workforce Intelligence Institute and be part of the
            transformation shaping the workforce of the next decade.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/contact">
              <button className="h-12 px-6 rounded-md bg-[#1E3A8A] text-white hover:bg-[#15307A] font-semibold inline-flex items-center gap-2">
                Contact the Institute <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/programs">
              <button className="h-12 px-6 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-[#FBFAF7] inline-flex items-center gap-2">
                <Award className="w-4 h-4" /> Apply to Zodiac
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function BioBlock({ icon: Icon, title, accent, children }) {
  return (
    <div className="zodiac-hairline-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47]">{title}</h3>
      </div>
      {children}
    </div>
  );
}
